<template>
  <app-layout title="Business Details">
    <form @submit.prevent="openSubmitModal">
      <!-- Sticky header -->
      <div class="border-b border-gray bg-white sticky top-0 z-10 p-2 lg:px-8">
        <div class="flex justify-between items-center">
          <div class="font-semibold text-lg">
            Business Details
          </div>

          <div class="flex items-center gap-x-2">
            <jet-button type="submit" class="h-8 leading-none" :disabled="form.processing || !canSubmit">
              Submit
            </jet-button>
          </div>
        </div>
      </div>
      <div class="px-8 pt-4">
        <div
          v-if="onboardingStatus === 'customer_rejected'"
          class="rounded px-4 py-3 text-sm border border-red-300 bg-red-50 text-red-800"
        >
          <div class="font-semibold mb-1">Your application has been rejected.</div>
          <div class="text-xs mt-0.5">Please contact your sales representative for further details.</div>
        </div>

        <div
          v-else-if="onboardingStatus === 'business_details_correction_required'"
          class="rounded px-4 py-3 text-sm border border-red-300 bg-red-50 text-red-800"
        >
          <div class="font-semibold mb-1">These business details were rejected due to:</div>
          <div class="text-sm">{{ rejectedReason }}</div><br>
          <div class="text-xs mt-0.5">Please contact your sales representative for further details.</div>
        </div>

        <div
          v-else-if="lastSubmittedAt && !isDirty"
          class="rounded px-4 py-3 text-sm border border-gray-200 bg-gray-50 text-gray-700"
        >
          <div class="font-medium text-gray-800">These details have already been submitted.</div>
          <div class="text-xs text-gray-600 mt-0.5">Submitted: {{ formattedSubmittedAt }}</div>
        </div>
      </div>

      <!-- Page body -->
      <div class="px-8 py-6">
        <customer-business-details
          :form="form"
          :customer="customer"
          :customerData="customerData"
          :onboarding-status="onboardingStatus"
          :payment-terms-options="paymentTermsOptions"
          :payment-method-options="paymentMethodOptions"
        />
      </div>
    </form>

    <jet-confirmation-modal :show="showSubmitConfirmModal" @close="closeSubmitModal">
      <template #title>
        Confirm submission
      </template>

      <template #content>
        <div class="text-sm text-gray-700">
          This information cannot be modified after submission. Are you sure you want to submit?
        </div>
      </template>

      <template #footer>
        <jet-button :disabled="form.processing" @click="confirmSubmit">
          Submit
        </jet-button>
        <jet-tertiary-button class="ml-2" :disabled="form.processing" @click="closeSubmitModal">
          Cancel
        </jet-tertiary-button>
      </template>
    </jet-confirmation-modal>
  </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3'
import { ref } from 'vue';
import AppLayout from '@/Layouts/AppLayout.vue'
import JetButton from '@/Jetstream/Button'
import JetTertiaryButton from '@/Jetstream/TertiaryButton'
import JetConfirmationModal from '@/Jetstream/ConfirmationModal'
import CustomerBusinessDetails from '@/Pages/Customer/Partials/CustomerBusinessDetails.vue'

export default {
  props: [
    'customer',
    'customerData',
    'onboardingStatus',
    'rejectedReason',
    'paymentTermsOptions',
    'paymentMethodOptions',
    'lastSubmittedAt',
    'submitRoute',
  ],

  components: {
    AppLayout,
    JetButton,
    JetTertiaryButton,
    JetConfirmationModal,
    CustomerBusinessDetails,
  },

  data() {
    return {
      showSubmitConfirmModal: false,
    }
  },

  setup(props) {
    const form = useForm({
        // used for submit + any guards you want later
        customer_id: props.customer?.id ?? null,

        // main form payload (shape can evolve; keeping it explicit is fine)
        business_name: props.customerData?.business_name ?? null,
        phone: props.customerData?.phone ?? null,
        business_contact: props.customerData?.business_contact ?? null,
        business_email: props.customerData?.business_email ?? null,
        years_in_business: props.customerData?.years_in_business ?? null,
        contact_name: props.customerData?.contact_name ?? null,
        ap_email: props.customerData?.ap_email ?? null,

        address_1: props.customerData?.address_1 ?? props.customer?.address_1 ?? null,
        city: props.customerData?.city ?? props.customer?.city ?? null,
        province: props.customerData?.province ?? props.customer?.province ?? null,
        postal_code: props.customerData?.postal_code ?? props.customer?.postal_code ?? null,

        exception_notes: props.customerData?.exception_notes ?? null,

        // trade refs (we’ll wire the exact field names later in the partial)
        no_trade_references: props.customerData?.no_trade_references ?? false,
        trade_reference_1: props.customerData?.trade_reference_1 ?? {
          company_name: null,
          company_address: null,
          contact_name: null,
          phone: null,
          email: null,
          account_opened_since: null,
          current_balance: null,
          },
        trade_reference_2: props.customerData?.trade_reference_2 ?? {
          company_name: null,
          company_address: null,
          contact_name: null,
          phone: null,
          email: null,
          account_opened_since: null,
          current_balance: null,
          },

        // payment preferences
        payment_method: props.customerData?.payment_method ?? null,
        payment_terms: props.customerData?.payment_terms ?? null,

        signature_name: props.customerData?.signature_name ?? null,
        signature_filename: props.customerData?.signature_filename ?? null,
    })

    const initial = ref(JSON.stringify(form.data()))
    return { form, initial }
  },

  methods: {
    openSubmitModal() {
      if (this.form.processing || !this.canSubmit) return
      this.showSubmitConfirmModal = true
    },
    closeSubmitModal() {
      this.showSubmitConfirmModal = false
    },
    confirmSubmit() {
      if (this.form.processing) return
      this.showSubmitConfirmModal = false
      this.submit()
    },
    submit() {
      this.form.post(this.submitRoute, {
        preserveScroll: false,
        onSuccess: () => {
          // reset dirty baseline after a successful submit
          this.initial = JSON.stringify(this.form.data())
          window.scrollTo({ top: 0, behavior: 'smooth' })
        },
      })
    },
    filled(value) {
      if (value === null || value === undefined) return false
      if (typeof value === 'string') return value.trim() !== ''
      return true
    },
    tradeReferenceFilled(ref) {
      if (!ref) return false
      return [
        'company_name',
        'company_address',
        'contact_name',
        'phone',
        'email',
        'account_opened_since',
        'current_balance',
      ].every((key) => this.filled(ref[key]))
    },
  },

  computed: {
    // Only allow submission when not yet submitted or when prior submission was rejected
    canSubmitByStatus() {
      return ['not_started', 'business_details_correction_required'].includes(this.onboardingStatus)
    },
    canSubmit() {
      if (!this.canSubmitByStatus) return false

      const f = this.form
      const requiredAlways = [
        f.business_name,
        f.phone,
        f.business_contact,
        f.business_email,
        f.years_in_business,
        f.contact_name,
        f.ap_email,
        f.address_1,
        f.city,
        f.province,
        f.postal_code,
        f.payment_method,
        f.payment_terms,
        f.signature_name,
        f.signature_filename,
      ]

      if (!requiredAlways.every((value) => this.filled(value))) return false

      if (f.payment_method === 'exception' && !this.filled(f.exception_notes)) return false

      if (!f.no_trade_references) {
        if (!this.tradeReferenceFilled(f.trade_reference_1)) return false
        if (!this.tradeReferenceFilled(f.trade_reference_2)) return false
      }

      return true
    },
    isDirty() {
      return JSON.stringify(this.form.data()) !== this.initial
    },
    formattedSubmittedAt() {
      if (!this.lastSubmittedAt) return ''
      const d = new Date(this.lastSubmittedAt)
      return isNaN(d.getTime()) ? this.lastSubmittedAt : d.toLocaleString()
    },
  },
}
</script>

<style scoped>
</style>
